import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import StyledModal from "styled-react-modal";
import _ from "lodash";

import { FilterParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { usersTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import { PaginatedParams } from "src/core/shared/api";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { setCustomer } from "src/core/customers/store/customers.actions";
import { setUser } from "src/core/users/store/users.actions";
import { ICustomer } from "src/core/customers/api/customers.api";
import {
  MoveUserModal,
  MoveUserModelOnSave
} from "src/core/users/components/MoveUserModal";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  ERPOdoo,
  ERPRJL,
  ERPSeedless,
  ERPUnity
} from "src/utils/constants";
import { IStore } from "src/core/shared/store";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { LikeToDo } from "src/library/components/LikeToDo";
import { CustomerUserExportModal } from "src/core/customers/components/CustomerUserExportModal";
import {
  ImportOrigin,
  ImportSettingType
} from "src/core/shared/api/importSetting.api";

export interface MoveUserModalData {
  customer: ICustomer;
  drop: IDrop;
  userId: number;
}

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

/**
 * Main customer users view
 *
 * @returns
 */
const CustomersUsersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology, history } = props;
  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const [ usersResponse, getUsers ] = useUserApi("LIST_USERS");
  const usersData = usersResponse.data;
  const [ showImportModal, setShowImportModal ] = React.useState(false);

  const { erp, importSettings } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    importSettings: global.settings?.importSettings
  }));

  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const checkPermission = useCheckUserPermission();
  const dispatch = useDispatch();
  const [ showMoveUserModal, setShowMoveUserModal ] = React.useState(false);
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ moveUserModelData, setMoveUserModelData ] = React.useState<MoveUserModalData | null>(null);
  const updateUserSuccessMessage = `The ${terminology.user.singular.text} has been successfully updated.`;
  const baseUpdateUserErrorMessage = `Could not update ${terminology.user.singular.text}`;

  const onFetchData = React.useCallback((filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>) => {
    getUsers({
      pagination,
      filters: {
        ...filters,
        customerId: { $not: null }
      }
    });
  }, [ getUsers ]);

  const canCreateUsers = React.useMemo(() => {
    return checkPermission("create-user");
  }, [ checkPermission ]);

  const editUserAction = (customer: ICustomer, userId: number) => {
    dispatch(setCustomer(customer));
    history.push(`/admin/customers/${customer.id}/users/${userId}/details`);
  };

  const resendInviteUserAction = (id: number) => {
    resendInvite({ id });
  };

  const handleSuspendUser = (user: any) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "suspended" : "active"
    };

    suspendUser(updatedUser);
  };

  // Handle suspend user update
  React.useEffect(() => {
    if (suspendUserResponse.data) {
      toast.success(updateUserSuccessMessage);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (suspendUserResponse.error) {
      toast.error(`${baseUpdateUserErrorMessage} - ${suspendUserResponse.error.message}`);
    }
  }, [
    baseUpdateUserErrorMessage,
    suspendUserResponse,
    terminology.user.singular.text,
    updateUserSuccessMessage
  ]);

  // Handle move user update
  React.useEffect(() => {
    if (updateUserResponse.data) {
      const userData: IUser = updateUserResponse.data;

      dispatch(setUser(userData));
      toast.success(updateUserSuccessMessage);
      history.push(`/admin/customers/${userData.customerId}/users/${userData.id}/drops`);
    }

    if (updateUserResponse.error) {
      toast.error(`${baseUpdateUserErrorMessage} - ${updateUserResponse.error.message}`);
    }
  }, [
    baseUpdateUserErrorMessage,
    dispatch,
    history,
    updateUserResponse,
    updateUserSuccessMessage
  ]);

  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse ]);

  const ImportExportUsers = React.useCallback(() => {
    const buttons = [
      <Button
        key="export-customer-user"
        onClick={() => {
          setShowExportModal(true);
        }}
      >
        {`Export ${terminology.user.plural.text}`}

      </Button>
    ];

    const hasUserCSVImportSetting = _.find(importSettings, is => is.type === ImportSettingType.User && is.origin === ImportOrigin.CSV && !is.options?.exportOnly);

    if (hasUserCSVImportSetting && (erp === ERPSeedless || erp === ERPRJL || erp === ERPUnity || erp === ERPOdoo)) {
      buttons.push((
        <Button
          key="import-users"
          onClick={() => setShowImportModal(true)}
        >
          {`Import ${terminology.user.plural.text}`}
        </Button>)
      );
    }

    return buttons;
  }, [
    erp,
    importSettings,
    terminology.user.plural.text
  ]);

  return (
    <Layout
      {...props}
      title={`Customer ${terminology.user.plural.text}`}
    >
      <MainContentWrapper
        fill
        Header={canCreateUsers && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={ImportExportUsers()}
          />
        )}
      >
        <Table
          tableId="global"
          ref={TableRef}
          error={usersResponse.error}
          loading={usersResponse.pending}
          tableOf={terminology.user.plural.text}
          data={usersData ? usersData.result : []}
          defaultFilters={{ customerId: null }}
          onFetchData={onFetchData}
          totalRows={usersData ? usersData.count : 0}
          pages={usersData ? usersData.pages : 0}
          defaultPagination={defaultPagination}
          mainAction={row => editUserAction(row.original.customer, row.original.id)}
          columns={usersTableColumns({
            type: "customer",
            terminology,
            ActionButton: row => {
              const canAmendUser = checkPermission("amend-user");

              if (!canAmendUser) {
                return null;
              }

              const items = [
                {
                  title: "Edit details",
                  action: () => editUserAction(row.original.customer, row.original.id)
                },
                {
                  title: `Edit ${terminology.drop.plural.text}`,
                  action: () => {
                    dispatch(setCustomer(row.original.customer));
                    history.push(`/admin/customers/${row.original.customerId}/users/${row.original.id}/drops`);
                  }
                },
                {
                  title: "Move user",
                  action: () => {
                    setMoveUserModelData({
                      customer: row.original.customer,
                      drop: row.original.branch,
                      userId: row.original.id
                    });
                    setShowMoveUserModal(true);
                  }
                },
                {
                  title: `${row.original.status === "active" ? "Suspend" : "Activate"}`,
                  action: () => {
                    handleSuspendUser(row.original);
                  }
                }
              ];

              if (row.original.lastLoggedIn === null) {
                items.push({
                  title: "Resend invite",
                  action: () => resendInviteUserAction(row.original.id)
                });
              }

              return (
                <ActionButton
                  title="Actions"
                  items={items}
                />
              );
            }
          })}
        />

        <StyledModal isOpen={showImportModal}>
          <ImportModal
            open={showImportModal}
            onDismiss={() => setShowImportModal(false)}
            type="user"
            terminology={props.terminology}
          />
        </StyledModal>

        <CustomerUserExportModal
          open={showExportModal}
          onDismiss={() => {
            setShowExportModal(false);
          }}
        />

        <MoveUserModal
          open={showMoveUserModal}
          onSave={(data: MoveUserModelOnSave) => {
            if (!data.defaultDrop) {
              return toast.error(`A default ${terminology.drop.singular.text} is required when moving a user to another ${terminology.customer.singular.text}.`);
            }

            if (!data.customerId) {
              return toast.error(`A ${terminology.customer.singular.text} is required when moving a user to another ${terminology.customer.singular.text}.`);
            }

            if (moveUserModelData?.userId) {
              updateUser({
                id: moveUserModelData?.userId,
                customerId: data.customerId,
                defaultBranchId: data.defaultDrop.id,
                userBranches: [ data.defaultDrop ]
              });
            }

            return;
          }}
          selectedCustomer={moveUserModelData?.customer}
          selectedDefaultDrop={moveUserModelData?.drop}
          terminology={terminology}
          onDismiss={() => {
            setShowMoveUserModal(false);
          }}
          isLoading={updateUserResponse.pending}
        />
      </MainContentWrapper>
    </Layout>
  );
};

export default CustomersUsersPage;
